import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, Typography, Tag, Space, Table, Result, Button, Spin, Alert, Tabs, Pagination, Empty, Collapse, Row, Col } from "antd";
import { ArrowLeftOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, LinkOutlined } from "@ant-design/icons";
import Loading from "../Loading";
import pushReport from "../../services/pushReport";
import apiCallback from "../../services/apiCallback";
import type { ColumnsType } from "antd/es/table";
import type { PushRequestItem } from "../../services/pushReport";
import type { CallbackItem } from "../../services/apiCallback";

const { Text } = Typography;
const { Panel } = Collapse;

/**
 * Formata uma data para exibição
 */
const formatDate = (dateString: string | null): string => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return dateString;
  }
};

/**
 * Calcula o tempo entre duas datas
 */
const calculateTimeBetween = (date1: string, date2: string): string => {
  try {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const diffMs = Math.abs(d2.getTime() - d1.getTime());
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} dia(s), ${diffHours % 24}h ${diffMinutes % 60}m`;
    } else if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes % 60}m ${diffSeconds % 60}s`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes}m ${diffSeconds % 60}s`;
    } else {
      return `${diffSeconds}s`;
    }
  } catch {
    return "-";
  }
};

/**
 * Página para exibir detalhes de requests e callbacks do Push.
 */
const PushDetailsContent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<PushRequestItem[]>([]);
  const [callbacks, setCallbacks] = useState<CallbackItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loadingCallbacks, setLoadingCallbacks] = useState(false);
  const [requestsPageSize, setRequestsPageSize] = useState(10);
  const [callbacksPageSize, setCallbacksPageSize] = useState(10);
  const [callbacksCurrentPage, setCallbacksCurrentPage] = useState(1);

  /**
   * Carrega os dados de requests e callbacks.
   */
  useEffect(() => {
    const loadData = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);

        // Buscar requests
        try {
          const requestsResponse = await pushReport.getReportRequests(id);
          if (requestsResponse.success && Array.isArray(requestsResponse.data)) {
            setRequests(requestsResponse.data);
          } else {
            setRequests([]);
          }
        } catch (requestError: any) {
          console.warn("Erro ao buscar requests:", requestError);
          // Se for 404, não é um erro crítico - apenas não há requests
          if (requestError.response?.status === 404) {
            setRequests([]);
          } else {
            throw requestError;
          }
        }

        // Buscar callbacks
        setLoadingCallbacks(true);
        try {
          const callbacksResponse = await apiCallback.getCallback(id);
          if (callbacksResponse.success && Array.isArray(callbacksResponse.data?.callbacks)) {
            setCallbacks(callbacksResponse.data.callbacks);
          } else {
            setCallbacks([]);
          }
        } catch (callbackError: any) {
          console.warn("Erro ao buscar callbacks:", callbackError);
          setCallbacks([]);
        } finally {
          setLoadingCallbacks(false);
        }
      } catch (err: any) {
        console.error("Erro ao buscar dados:", err);
        setError(err.message || "Erro ao buscar dados");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  /**
   * Handler para voltar.
   */
  const handleBack = () => {
    window.close();
    // Se não conseguir fechar (não foi aberto em nova aba), volta para buscar-fonte
    if (window.opener) {
      window.close();
    } else {
      window.history.back();
    }
  };

  /**
   * Handler para recarregar dados.
   */
  const handleReload = () => {
    window.location.reload();
  };

  /**
   * Calcula o tempo entre cadastros
   */
  const getTimeBetweenRegistrations = (): string[] => {
    const cadastros = requests
      .filter((req) => req.type === "cadastro")
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    if (cadastros.length < 2) {
      return [];
    }

    const times: string[] = [];
    for (let i = 1; i < cadastros.length; i++) {
      const timeBetween = calculateTimeBetween(cadastros[i - 1].createdAt, cadastros[i].createdAt);
      times.push(
        `Entre ${formatDate(cadastros[i - 1].createdAt)} e ${formatDate(cadastros[i].createdAt)}: ${timeBetween}`
      );
    }

    return times;
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Result
        status="error"
        title="Erro ao buscar dados"
        subTitle={<Text copyable>{error}</Text>}
        extra={[
          <Button type="primary" key="back" onClick={handleBack} icon={<ArrowLeftOutlined />}>
            Voltar
          </Button>,
          <Button key="retry" onClick={handleReload} icon={<ReloadOutlined />}>
            Tentar novamente
          </Button>,
        ]}
      />
    );
  }

  /**
   * Calcula o tempo de retorno baseado no status
   */
  const getReturnTime = (status: string, createdAt: string, updatedAt: string): string => {
    if (status === "pending") {
      return "Pendente";
    }
    return calculateTimeBetween(createdAt, updatedAt);
  };

  /**
   * Agrupa requests por tipo e período de tempo
   */
  const groupRequestsByTimeWindow = (
    requests: PushRequestItem[],
    type: string,
    timeWindowMinutes: number
  ): PushRequestItem[][] => {
    const filteredRequests = requests
      .filter((r) => r.type === type)
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    if (filteredRequests.length === 0) return [];

    const groups: PushRequestItem[][] = [];
    let currentGroup: PushRequestItem[] = [filteredRequests[0]];

    for (let i = 1; i < filteredRequests.length; i++) {
      const prevRequest = filteredRequests[i - 1];
      const currentRequest = filteredRequests[i];
      const timeDiff = new Date(currentRequest.createdAt).getTime() - new Date(prevRequest.createdAt).getTime();
      const timeDiffMinutes = timeDiff / (1000 * 60);

      if (timeDiffMinutes <= timeWindowMinutes) {
        currentGroup.push(currentRequest);
      } else {
        groups.push(currentGroup);
        currentGroup = [currentRequest];
      }
    }
    groups.push(currentGroup);

    return groups.reverse(); // Retorna todos os grupos (mais recentes primeiro)
  };

  /**
   * Agrupa requests por tipo e janela de tempo
   */
  const groupRequestsByTypeAndTime = (): PushRequestItem[][] => {
    const allGroups: PushRequestItem[][] = [];

    // Agrupa monitoramentos (5 minutos)
    const monitoramentoGroups = groupRequestsByTimeWindow(requests, "monitoramento", 5);
    allGroups.push(...monitoramentoGroups);

    // Agrupa cadastros (5 minutos)
    const cadastroGroups = groupRequestsByTimeWindow(requests, "cadastro", 5);
    allGroups.push(...cadastroGroups);

    // Agrupa reprocessamentos (2 minutos, como na análise)
    const reprocessamentoGroups = groupRequestsByTimeWindow(requests, "reprocessamento", 2);
    allGroups.push(...reprocessamentoGroups);

    // Ordena todos os grupos por data do primeiro request (mais recente primeiro)
    allGroups.sort((a, b) => {
      const dateA = new Date(a[0].createdAt).getTime();
      const dateB = new Date(b[0].createdAt).getTime();
      return dateB - dateA;
    });

    return allGroups;
  };

  /**
   * Processa os dados para a aba de análise
   */
  const getAnalysisData = () => {
    // 1. Últimos 5 monitoramentos
    const monitoramentoGroups = groupRequestsByTimeWindow(requests, "monitoramento", 5);
    // Calcular tempos entre monitoramentos para cada um
    const monitoramentoTimes: (string | null)[] = [];
    for (let i = 0; i < monitoramentoGroups.length; i++) {
      if (i < monitoramentoGroups.length - 1) {
        const currentGroup = monitoramentoGroups[i]; // Monitoramento atual
        const nextGroup = monitoramentoGroups[i + 1]; // Próximo monitoramento
        const currentLastRequest = currentGroup[currentGroup.length - 1];
        const nextFirstRequest = nextGroup[0];
        // Tempo do fim do monitoramento atual até o início do próximo
        const timeBetween = calculateTimeBetween(currentLastRequest.updatedAt, nextFirstRequest.createdAt);
        monitoramentoTimes.push(timeBetween);
      } else {
        // Último monitoramento não tem próximo
        monitoramentoTimes.push(null);
      }
    }

    // 2. Verificar reprocessamentos nos últimos 5 monitoramentos
    const lastMonitoramentoTime = monitoramentoGroups.length > 0
      ? new Date(monitoramentoGroups[monitoramentoGroups.length - 1][0].createdAt).getTime()
      : null;

    let reprocessamentoGroups: PushRequestItem[][] = [];
    if (lastMonitoramentoTime) {
      const reprocessamentosInPeriod = requests.filter(
        (r) =>
          r.type === "reprocessamento" &&
          new Date(r.createdAt).getTime() >= lastMonitoramentoTime
      );
      if (reprocessamentosInPeriod.length > 0) {
        reprocessamentoGroups = groupRequestsByTimeWindow(requests, "reprocessamento", 2).slice(0, 5);
      }
    }

    // 3. Últimos 5 callbacks
    const lastCallbacks = callbacks
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5);

    // 4. Abrangências mais rápidas e mais lentas
    const abrangenciaStats: Record<
      string,
      { platform: string; search: string; query: string; times: number[]; totalTime: number }
    > = {};

    requests.forEach((req) => {
      if (req.status === "success" && req.platform && req.search && req.query) {
        const key = `${req.platform}-${req.search}-${req.query}`;
        const timeMs = new Date(req.updatedAt).getTime() - new Date(req.createdAt).getTime();
        const timeSeconds = timeMs / 1000;

        if (!abrangenciaStats[key]) {
          abrangenciaStats[key] = {
            platform: req.platform,
            search: req.search,
            query: req.query,
            times: [],
            totalTime: 0,
          };
        }
        abrangenciaStats[key].times.push(timeSeconds);
        abrangenciaStats[key].totalTime += timeSeconds;
      }
    });

    const abrangenciaArray = Object.values(abrangenciaStats).map((stat) => ({
      ...stat,
      averageTime: stat.totalTime / stat.times.length,
      count: stat.times.length,
    }));

    const fastestAbrangencia = abrangenciaArray.length > 0
      ? abrangenciaArray.reduce((prev, curr) => (prev.averageTime < curr.averageTime ? prev : curr))
      : null;

    const slowestAbrangencia = abrangenciaArray.length > 0
      ? abrangenciaArray.reduce((prev, curr) => (prev.averageTime > curr.averageTime ? prev : curr))
      : null;

    return {
      monitoramentoGroups,
      monitoramentoTimes, // Array de tempos, onde cada índice corresponde ao monitoramento do mesmo índice
      reprocessamentoGroups,
      lastCallbacks,
      fastestAbrangencia,
      slowestAbrangencia,
    };
  };

  /**
   * Colunas da tabela de requests
   */
  const requestColumns: ColumnsType<PushRequestItem> = [
    {
      title: "Tipo / Plataforma",
      key: "type-platform",
      width: 200,
      render: (_, record: PushRequestItem) => {
        const colorMap: Record<string, string> = {
          cadastro: "blue",
          reprocessamento: "orange",
          monitoramento: "green",
        };
        const labelMap: Record<string, string> = {
          cadastro: "Cadastro",
          reprocessamento: "Reprocessamento",
          monitoramento: "Monitoramento",
        };
        return (
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Tag color={colorMap[record.type] || "default"}>{labelMap[record.type] || record.type}</Tag>
            <Space wrap size="small" style={{ fontSize: "12px" }}>
              {record.platform && <Tag color="cyan">{record.platform}</Tag>}
              {record.search && <Tag color="volcano">{record.search}</Tag>}
              {record.query && <Tag color="purple">{record.query}</Tag>}
              {!record.platform && !record.search && !record.query && <Text type="secondary" style={{ fontSize: "12px" }}>-</Text>}
            </Space>
          </Space>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status: string) => {
        const colorMap: Record<string, string> = {
          success: "success",
          pending: "processing",
          error: "error",
          warning: "warning",
        };
        return <Tag color={colorMap[status] || "default"}>{status}</Tag>;
      },
    },
    {
      title: "Request ID",
      dataIndex: "requestId",
      key: "requestId",
      width: 250,
      render: (requestId: string | null) => (requestId ? <Text copyable code style={{ fontSize: "12px" }}>{requestId}</Text> : <Text type="secondary">-</Text>),
    },
    {
      title: "Datas",
      key: "dates",
      width: 180,
      render: (_, record: PushRequestItem) => (
        <Space direction="vertical" size="small" style={{ fontSize: "12px" }}>
          <div>
            <Text type="secondary" style={{ fontSize: "11px" }}>Criação:</Text>
            <br />
            <Text>{formatDate(record.createdAt)}</Text>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: "11px" }}>Atualização:</Text>
            <br />
            <Text>{formatDate(record.updatedAt)}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Tempo de Retorno",
      key: "returnTime",
      width: 150,
      render: (_, record: PushRequestItem) => {
        const returnTime = getReturnTime(record.status, record.createdAt, record.updatedAt);
        return (
          <Text style={{ fontSize: "12px" }} type={record.status === "pending" ? "secondary" : undefined}>
            {returnTime}
          </Text>
        );
      },
    },
  ];


  const timeBetweenRegistrations = getTimeBetweenRegistrations();

  return (
    <Space direction="vertical" size="large" style={{ width: "100%", padding: "24px" }}>
      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <Text copyable code style={{ fontSize: "18px" }}>
                {id}
              </Text>
              <Tag color="blue">Push</Tag>
            </div>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={handleReload}>
                Recarregar
              </Button>
              <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
                Fechar
              </Button>
            </Space>
          </div>
        </Space>
      </Card>

      {/* Requests e Callbacks em Tabs */}
      <Card>
        <Tabs
          defaultActiveKey="requests"
          items={[
            {
              key: "requests",
              label: "Requests",
              children: (() => {
                const groupedRequests = groupRequestsByTypeAndTime();
                const typeLabels: Record<string, string> = {
                  cadastro: "Cadastro",
                  reprocessamento: "Reprocessamento",
                  monitoramento: "Monitoramento",
                };
                const typeColors: Record<string, string> = {
                  cadastro: "blue",
                  reprocessamento: "orange",
                  monitoramento: "green",
                };

                return (
                  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <div>
                      <Text strong style={{ fontSize: "14px", marginRight: 8 }}>Total por tipo:</Text>
                      <Space size="small">
                        <Tag color="blue">Cadastros: {requests.filter((r) => r.type === "cadastro").length}</Tag>
                        <Tag color="orange">Reprocessamentos: {requests.filter((r) => r.type === "reprocessamento").length}</Tag>
                        <Tag color="green">Monitoramentos: {requests.filter((r) => r.type === "monitoramento").length}</Tag>
                      </Space>
                    </div>
                    <Alert
                      message="Mostrando apenas as 100 últimas requests capturadas"
                      type="info"
                      showIcon
                    />
                    {requests.length === 0 ? (
                      <Alert message="Nenhuma request encontrada" type="info" showIcon />
                    ) : (
                      <Collapse>
                        {groupedRequests.map((groupRequests, groupIdx) => {
                          if (groupRequests.length === 0) return null;
                          
                          const firstRequest = groupRequests[0];
                          const lastRequest = groupRequests[groupRequests.length - 1];
                          const totalTime = calculateTimeBetween(firstRequest.createdAt, lastRequest.updatedAt);
                          const type = firstRequest.type || "cadastro";
                          
                          // Calcula tempo entre este grupo e o grupo anterior na lista (independente do tipo)
                          let timeFromPrevious = null;
                          if (groupIdx > 0) {
                            const previousGroup = groupedRequests[groupIdx - 1];
                            if (previousGroup && previousGroup.length > 0) {
                              const previousLastRequest = previousGroup[previousGroup.length - 1];
                              // Tempo do fim do grupo anterior até o início do grupo atual
                              timeFromPrevious = calculateTimeBetween(previousLastRequest.updatedAt, firstRequest.createdAt);
                            }
                          }
                          
                          // Calcula estatísticas do grupo
                          const successCount = groupRequests.filter((r) => r.status === "success").length;
                          const errorCount = groupRequests.filter((r) => r.status === "error").length;
                          const warningCount = groupRequests.filter((r) => r.status === "warning").length;
                          const pendingCount = groupRequests.filter((r) => r.status === "pending").length;

                          // Para todos os tipos (monitoramento, reprocessamento e cadastro), mostra apenas o tipo com tag colorida
                          return (
                            <Panel
                              key={groupIdx}
                              header={
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", flexWrap: "wrap", gap: "8px" }}>
                                  <Space wrap>
                                    <Tag color={typeColors[type]} style={{ fontSize: "13px", padding: "4px 12px" }}>
                                      {typeLabels[type]}: {groupRequests.length}
                                    </Tag>
                                    <Text type="secondary" style={{ fontSize: "12px" }}>
                                      {formatDate(firstRequest.createdAt)}
                                    </Text>
                                    {timeFromPrevious && (
                                      <Text type="secondary" style={{ fontSize: "12px" }}>
                                        | {timeFromPrevious} desde o anterior
                                      </Text>
                                    )}
                                  </Space>
                                  <Space>
                                    {successCount > 0 && <Tag color="success">{successCount}</Tag>}
                                    {warningCount > 0 && <Tag color="warning">{warningCount}</Tag>}
                                    {errorCount > 0 && <Tag color="error">{errorCount}</Tag>}
                                    {pendingCount > 0 && <Tag color="processing">{pendingCount}</Tag>}
                                  </Space>
                                </div>
                              }
                            >
                              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                                <div>
                                  <Text type="secondary" style={{ fontSize: "12px" }}>
                                    Início: {formatDate(firstRequest.createdAt)}
                                  </Text>
                                  <br />
                                  <Text type="secondary" style={{ fontSize: "12px" }}>
                                    Fim: {formatDate(lastRequest.updatedAt)}
                                  </Text>
                                  <br />
                                  <Text strong>Tempo total: {totalTime}</Text>
                                </div>
                                <Table
                                  columns={requestColumns}
                                  dataSource={groupRequests}
                                  rowKey="cnjParamReqId"
                                  pagination={false}
                                  size="small"
                                />
                              </Space>
                            </Panel>
                          );
                        })}
                      </Collapse>
                    )}
                  </Space>
                );
              })(),
            },
            {
              key: "analysis",
              label: "Análise",
              children: (() => {
                const analysisData = getAnalysisData();
                const { Panel } = Collapse;
                
                // Verificar status de cada grupo de monitoramento
                const getMonitoramentoStatus = (group: PushRequestItem[]) => {
                  const hasPending = group.some((r) => r.status === "pending");
                  return hasPending ? "pending" : "finalized";
                };

                return (
                  <Space direction="vertical" size="large" style={{ width: "100%" }}>
                    {/* 1. Últimos 5 monitoramentos */}
                    <Card title="Últimos 5 Monitoramentos" size="small">
                      {analysisData.monitoramentoGroups.length === 0 ? (
                        <Alert message="Nenhum monitoramento encontrado" type="info" showIcon />
                      ) : (
                        <Collapse>
                          {analysisData.monitoramentoGroups.map((group, idx) => {
                            const firstRequest = group[0];
                            const lastRequest = group[group.length - 1];
                            const totalTime = calculateTimeBetween(firstRequest.createdAt, lastRequest.updatedAt);
                            const status = getMonitoramentoStatus(group);
                            const monitoramentoNumber = idx + 1; // Mais recente é 1
                            const timeToNext = analysisData.monitoramentoTimes[idx];
                            
                            return (
                              <Panel
                                key={idx}
                                header={
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                    <Space>
                                      <Text strong>Monitoramento {monitoramentoNumber}</Text>
                                      <Text type="secondary">({group.length} request(s))</Text>
                                      {status === "pending" ? (
                                        <Tag color="processing">Pendente</Tag>
                                      ) : (
                                        <Tag color="success">Finalizada</Tag>
                                      )}
                                    </Space>
                                    {timeToNext && (
                                      <Text type="secondary" style={{ fontSize: "12px" }}>
                                        Criado {timeToNext} depois do monitoramento {monitoramentoNumber + 1}
                                      </Text>
                                    )}
                                  </div>
                                }
                              >
                                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                                  <div>
                                    <Text type="secondary" style={{ fontSize: "12px" }}>
                                      Início: {formatDate(firstRequest.createdAt)}
                                    </Text>
                                    <br />
                                    <Text type="secondary" style={{ fontSize: "12px" }}>
                                      Fim: {formatDate(lastRequest.updatedAt)}
                                    </Text>
                                    <br />
                                    <Text strong>Tempo total: {totalTime}</Text>
                                  </div>
                                  <div style={{ marginTop: 16 }}>
                                    <Text strong style={{ fontSize: "14px" }}>Requests:</Text>
                                    <Table
                                      columns={requestColumns}
                                      dataSource={group}
                                      rowKey="cnjParamReqId"
                                      pagination={false}
                                      size="small"
                                      style={{ marginTop: 8 }}
                                    />
                                  </div>
                                </Space>
                              </Panel>
                            );
                          })}
                        </Collapse>
                      )}
                    </Card>

                    {/* 2. Últimos 5 reprocessamentos (se existirem) */}
                    {analysisData.reprocessamentoGroups.length > 0 && (
                      <Card title="Últimos 5 Reprocessamentos" size="small">
                        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                          {analysisData.reprocessamentoGroups.map((group, idx) => {
                            const firstRequest = group[0];
                            const lastRequest = group[group.length - 1];
                            const totalTime = calculateTimeBetween(firstRequest.createdAt, lastRequest.updatedAt);
                            return (
                              <Card key={idx} size="small" style={{ borderLeft: "4px solid #faad14" }}>
                                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                                  <div>
                                    <Text strong>Reprocessamento {analysisData.reprocessamentoGroups.length - idx}:</Text>
                                    <Text type="secondary" style={{ marginLeft: 8 }}>
                                      {group.length} request(s)
                                    </Text>
                                  </div>
                                  <div>
                                    <Text type="secondary" style={{ fontSize: "12px" }}>
                                      Início: {formatDate(firstRequest.createdAt)}
                                    </Text>
                                    <br />
                                    <Text type="secondary" style={{ fontSize: "12px" }}>
                                      Fim: {formatDate(lastRequest.updatedAt)}
                                    </Text>
                                    <br />
                                    <Text strong>Tempo total: {totalTime}</Text>
                                  </div>
                                </Space>
                              </Card>
                            );
                          })}
                        </Space>
                      </Card>
                    )}

                    {/* 3. Tempo entre Cadastros */}
                    {timeBetweenRegistrations.length > 0 && (
                      <Card title="Tempo entre Cadastros" size="small">
                        <Space direction="vertical" size="small" style={{ width: "100%" }}>
                          {timeBetweenRegistrations.map((time, index) => (
                            <Text key={index} style={{ fontSize: "13px" }}>{time}</Text>
                          ))}
                        </Space>
                      </Card>
                    )}

                    {/* 4. Últimos 5 callbacks */}
                    <Card title="Últimos 5 Callbacks" size="small">
                      {analysisData.lastCallbacks.length === 0 ? (
                        <Alert message="Nenhum callback encontrado" type="info" showIcon />
                      ) : (
                        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                          {analysisData.lastCallbacks.map((callback, idx) => {
                            const isSuccess = callback.status === "success";
                            return (
                              <Card key={idx} size="small">
                                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
                                    <Tag color="purple">{callback.application}</Tag>
                                    <Tag color={isSuccess ? "success" : "error"} icon={isSuccess ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                                      {callback.status}
                                    </Tag>
                                    <Text type="secondary" style={{ fontSize: "12px" }}>
                                      {formatDate(callback.createdAt)}
                                    </Text>
                                  </div>
                                  {callback.urlClient && (
                                    <Text copyable ellipsis={{ tooltip: callback.urlClient }} style={{ fontSize: "12px", wordBreak: "break-word", color: "inherit" }}>
                                      {callback.urlClient}
                                    </Text>
                                  )}
                                  {callback.file && (
                                    <div>
                                      <a href={callback.file} target="_blank" rel="noopener noreferrer">
                                        <LinkOutlined /> Abrir arquivo
                                      </a>
                                    </div>
                                  )}
                                </Space>
                              </Card>
                            );
                          })}
                        </Space>
                      )}
                    </Card>

                    {/* 4. Abrangências mais rápidas e mais lentas */}
                    <Card title="Análise de Abrangências" size="small">
                      {analysisData.fastestAbrangencia && analysisData.slowestAbrangencia ? (
                        <Row gutter={16}>
                          <Col xs={24} sm={12}>
                            <div>
                              <Text strong style={{ color: "#52c41a" }}>Mais Rápida:</Text>
                              <Card size="small" style={{ marginTop: 8, borderLeft: "4px solid #52c41a" }}>
                                <Space direction="vertical" size="small">
                                  <div>
                                    <Tag color="cyan">{analysisData.fastestAbrangencia.platform}</Tag>
                                    <Tag color="volcano">{analysisData.fastestAbrangencia.search}</Tag>
                                    <Tag color="purple">{analysisData.fastestAbrangencia.query}</Tag>
                                  </div>
                                  <div>
                                    <Text>Tempo médio: {analysisData.fastestAbrangencia.averageTime.toFixed(2)}s</Text>
                                    <br />
                                    <Text type="secondary" style={{ fontSize: "12px" }}>
                                      Total de requests: {analysisData.fastestAbrangencia.count}
                                    </Text>
                                  </div>
                                </Space>
                              </Card>
                            </div>
                          </Col>
                          <Col xs={24} sm={12}>
                            <div>
                              <Text strong style={{ color: "#ff4d4f" }}>Mais Lenta:</Text>
                              <Card size="small" style={{ marginTop: 8, borderLeft: "4px solid #ff4d4f" }}>
                                <Space direction="vertical" size="small">
                                  <div>
                                    <Tag color="cyan">{analysisData.slowestAbrangencia.platform}</Tag>
                                    <Tag color="volcano">{analysisData.slowestAbrangencia.search}</Tag>
                                    <Tag color="purple">{analysisData.slowestAbrangencia.query}</Tag>
                                  </div>
                                  <div>
                                    <Text>Tempo médio: {analysisData.slowestAbrangencia.averageTime.toFixed(2)}s</Text>
                                    <br />
                                    <Text type="secondary" style={{ fontSize: "12px" }}>
                                      Total de requests: {analysisData.slowestAbrangencia.count}
                                    </Text>
                                  </div>
                                </Space>
                              </Card>
                            </div>
                          </Col>
                        </Row>
                      ) : (
                        <Alert message="Dados insuficientes para análise de abrangências" type="info" showIcon />
                      )}
                    </Card>
                  </Space>
                );
              })(),
            },
            {
              key: "callbacks",
              label: "Callbacks",
              children: (
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                  <div>
                    <Text strong style={{ fontSize: "14px", marginRight: 8 }}>Total:</Text>
                    <Tag color="blue">{callbacks.length}</Tag>
                  </div>
                  {loadingCallbacks ? (
                    <Spin />
                  ) : callbacks.length === 0 ? (
                    <Empty description="Nenhum callback encontrado" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  ) : (
                    <>
                      <div>
                        {(() => {
                          const startIndex = (callbacksCurrentPage - 1) * callbacksPageSize;
                          const endIndex = startIndex + callbacksPageSize;
                          const currentCallbacks = callbacks.slice(startIndex, endIndex);

                          return currentCallbacks.map((callback: CallbackItem, callbackIdx: number) => {
                            const isSuccess = callback.status === "success";
                            return (
                              <Card key={callbackIdx} size="small" style={{ marginBottom: 16 }}>
                                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                                  {/* Linha 1: Application (destacada), Status, Data de criação */}
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
                                    <Tag color="purple" style={{ fontSize: "14px", padding: "4px 12px" }}>
                                      {callback.application}
                                    </Tag>
                                    <Tag color={isSuccess ? "success" : "error"} icon={isSuccess ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                                      {callback.status}
                                    </Tag>
                                    <Text type="secondary">{formatDate(callback.createdAt)}</Text>
                                  </div>

                                  {/* Linha 2: Method (destacado), URL, Retry, Tempo total */}
                                  {callback.urlClient && (
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
                                      <Tag color="blue">{callback.method}</Tag>
                                      <Text copyable ellipsis={{ tooltip: callback.urlClient }} style={{ wordBreak: "break-word" }}>
                                        {callback.urlClient}
                                      </Text>
                                      <Text type="secondary">|</Text>
                                      <Text type="secondary">retry: {callback.retry}</Text>
                                      <Text type="secondary">|</Text>
                                      <Text type="secondary">Tempo (s): {callback.timeToSendSeconds.toFixed(3)}</Text>
                                    </div>
                                  )}

                                  {/* Linha 3: Arquivo */}
                                  {callback.file && (
                                    <div>
                                      <a href={callback.file} target="_blank" rel="noopener noreferrer">
                                        <LinkOutlined /> Abrir arquivo
                                      </a>
                                    </div>
                                  )}
                                </Space>
                              </Card>
                            );
                          });
                        })()}
                      </div>

                      {callbacks.length > callbacksPageSize && (
                        <div style={{ marginTop: 16, textAlign: "right" }}>
                          <Pagination
                            current={callbacksCurrentPage}
                            pageSize={callbacksPageSize}
                            total={callbacks.length}
                            showSizeChanger
                            showTotal={(total, range) => `${range[0]}-${range[1]} de ${total} callbacks`}
                            pageSizeOptions={["10", "25", "50", "100"]}
                            onChange={(page, size) => {
                              setCallbacksCurrentPage(page);
                              setCallbacksPageSize(size);
                            }}
                            onShowSizeChange={(current, size) => {
                              setCallbacksCurrentPage(1);
                              setCallbacksPageSize(size);
                            }}
                          />
                        </div>
                      )}
                    </>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </Card>
    </Space>
  );
};

/**
 * Componente principal da página PushDetails.
 */
const PushDetails: React.FC = () => {
  return (
    <PushDetailsContent />
  );
};

export default PushDetails;
